import React, { FC, useEffect, useState, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { fetchTaskLabelGroupOrder, updateTaskLabelGroupOrder } from '../../../../states/containers/task/taskSlice'
import { RootState } from '../../../../states/containers/store'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { format } from '../../../../utils/date'
import Holidays from 'date-holidays'
import LoadingSquare from '../../../components/common/LoadingSquare'
import {
  DndContext,
  PointerSensor,
  useSensors,
  useSensor,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
} from '@dnd-kit/core'
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { sortBy, uniqueId } from 'lodash'
import { Link, useHistory, useParams } from 'react-router-dom'
import { setPath } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { URL } from '../../../constants/url'
import { updateTaskLabelSearchQuery, updateTaskSearchQuery } from '../../../../states/containers/common/currentSlice'

interface orderModel {
  id: number
  name: string
  code: string | null
  color: string
  disabled: boolean
  disable_status: 'completed' | 'deleted' | null
  scheduled_start_date: string | null
  scheduled_end_date: string | null
  created_at: string
  created_by: string | null
  order_index: number
}

type RowProps = {
  id: number
  name: string
  color: string
  disable_status: 'completed' | 'deleted' | null
  startDate: string | null
  endDate: string | null
  createdBy: string | null
  orderIndex: number
  isSelected: boolean
  onClick: (e: React.MouseEvent) => void
}

type LocationState = {
  labelGroupSetting?: boolean;
}

const Index: FC = () => {
  const { task } = useSelector((state: RootState) => state)
  const dispatch = useDispatch()
  const history = useHistory()
  const { language } = useParams<{ language: LangValue }>()

  const [selectedRange, setSelectedRange] = useState<{lo: number, hi: number} | null>(null)
  const [dragIds, setDragIds] = useState<number[]>([])
  const [activeId, setActiveId] = useState<number | null>(null)
  const [ghostIds, setGhostIds] = useState<number[]>([])
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  )
  const [pollingTrigger, setPollingTrigger] = useState<string>("")
  const taskLabelGroupOrder: orderModel[] = useMemo(() => sortBy(task.taskLabelGroupOrder?.label_groups, 'order_index'), [task.taskLabelGroupOrder])
  const holidays = useMemo(() => {
    const h = new Holidays()
    h.init('JP')
    return h
  }, [])

  const locationState = history.location.state as LocationState

  const calendarColor = (day: string) => {
    const holiday = holidays.isHoliday(new Date(day))
    if ((holiday && holiday[0].type === "public") || format(day, 'd') === '0') {
      return "#FF0000"
    }
    if (format(day, 'd') === '6') {
      return "#317AB7"
    }
    return "#666666"
  }

  const dayOfWeek = (day: string | undefined) => {
    if (!day) return ""
    const holiday = holidays.isHoliday(new Date(day))
    let dd = format(day, "dd", "ja")
    if (holiday && holiday[0].type === "public") {
      dd += "・祝"
    }
    return (
      <>
        (
        <span style={{ color: calendarColor(day) }}>
          {dd}
        </span>
        )
      </>
    )
  }

  const SortableRow = ({ id, name, color, disable_status, startDate, endDate, createdBy, orderIndex, isSelected, onClick }: RowProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, data: { orderIndex } })

    return (
      <tr
        ref={setNodeRef}
        className={[
          isSelected ? 'is-selected' : '',
          isDragging ? 'is-dragging' : '',
        ].join(' ')}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
        }}
        onClick={onClick}
      >
        <td {...attributes} {...listeners} className="drag-handle">
          <div className="icon">
            <FontAwesomeIcon icon={['fas', 'grip-vertical']} />
          </div>
        </td>
        {
          dragIds.length >= 2 && isDragging ? (
            <td colSpan={3}>
              {dragIds.length}
              件のプロジェクト
            </td>
          ) : (
            <>
              <td>
                <div className="project-title">
                  <div className="color-circle" style={{ backgroundColor: color }} />
                  <div>{name}</div>
                  {
                    disable_status === "completed" && (
                    <div className="completed">
                      完了済
                    </div>
                    )
                  }
                </div>
              </td>
              <td>
                {
                  startDate ? (
                    <>
                      {format(startDate, "YYYY/M/D")}
                      {dayOfWeek(startDate)}
                    </>
                  ) : (
                    <>
                      未設定
                    </>
                  )
                }
                &nbsp;~&nbsp;
                {
                  endDate ? (
                    <>
                      {format(endDate, "YYYY/M/D")}
                      {dayOfWeek(endDate)}
                    </>
                  ) : (
                    <>
                      未設定
                    </>
                  )
                }
              </td>
              <td>{createdBy}</td>
            </>
          )
        }
      </tr>
    )
  }

  const overlay = useMemo(() => {
    if (!activeId) return null
    const headId = dragIds.length > 0 ? dragIds[0] : Number(activeId)
    const row = taskLabelGroupOrder?.find((v) => v.id === headId)
    if (!row) return null
    return (
      <div className="drag-overlay">
        <div className="overlay-count">
          {dragIds.length}
        </div>
        <div className="overlay-row">
          { dragIds.length === 1 ? (
            <>
              <div className="color-circle" style={{ backgroundColor: row.color }} />
              <span>
                {row.name}
              </span>
            </>
          ) : (
            <>
              {dragIds.length}
              件のプロジェクト
            </>
          )}
        </div>
      </div>
    )
  }, [activeId, dragIds, taskLabelGroupOrder])

  const handleRowClick = (e: React.MouseEvent, id: number, index: number) => {
    if (e.shiftKey && selectedRange) {
      const lo = Math.min(selectedRange.lo, index)
      const hi = Math.max(selectedRange.hi, index)
      setSelectedRange({ lo, hi })
    } else {
      setSelectedRange({ lo: index, hi: index })
    }
  }

  const orderRangeToId = (range: { lo: number; hi: number }): number[] => {
    if (!range) return []
    const sliced = taskLabelGroupOrder?.slice(range.lo, range.hi + 1) ?? []
    return sliced.map((v) => v.id)
  }

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(Number(active.id))
    const activeIndex = active.data.current?.sortable?.index as number | undefined
    if (activeIndex == null) {
      setDragIds([])
      return
    }
    let useRange = { lo: activeIndex, hi: activeIndex }
    if (!!selectedRange && activeIndex >= selectedRange.lo && activeIndex <= selectedRange.hi) {
      useRange = selectedRange
    }
    setSelectedRange(useRange)
    setDragIds(orderRangeToId(useRange))
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) {
      setActiveId(null)
      setDragIds([])
      return
    }
    const toIndex = over.data.current?.orderIndex as number | undefined
    if (toIndex === undefined) {
      setActiveId(null)
      setDragIds([])
      return
    }
    if (toIndex === active.data.current?.orderIndex) {
      setActiveId(null)
      setDragIds([])
      setSelectedRange(null)
      return
    }
    const ids = dragIds.length > 0 ? dragIds : [Number(active.id)]

    dispatch(
      updateTaskLabelGroupOrder(
        {
          label_group_ids: ids,
          to_order_index: toIndex,
        },
        () => {
          dispatch(updateTaskSearchQuery("?key=order_index"))
          dispatch(updateTaskLabelSearchQuery("?key=order_index"))
        },
      ),
    )
    setActiveId(null)
    setDragIds([])
    setSelectedRange(null)
  }

  useEffect(() => {
    dispatch(fetchTaskLabelGroupOrder())
  }, [])

  useEffect(() => {
    if (!activeId || dragIds.length === 0) {
      setGhostIds([])
    } else {
      setGhostIds(dragIds.filter((id) => id !== activeId))
    }
  }, [activeId, dragIds])

  useEffect(() => {
    if (!pollingTrigger) return
    const polling = setTimeout(() => {
      dispatch(fetchTaskLabelGroupOrder())
    }, 10 * 1000)
    // eslint-disable-next-line consistent-return
    return () => {
      if (pollingTrigger) {
        clearTimeout(polling)
      }
    }
  }, [pollingTrigger])

  useEffect(() => {
    setPollingTrigger(!task.taskLabelGroupOrder?.importing_project ? "" : uniqueId())
  }, [task.taskLabelGroupOrder])

  return (
    <section className="task_page task_status">
      <Helmet>
        <title>
          {
            locationState?.labelGroupSetting ? (
              `Qasee - 工数管理 - プロジェクト設定 - プロジェクト並び替え`
            ) : (
              `Qasee - 工数管理 - プロジェクトレポート - プロジェクト並び替え`
            )
          }
        </title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        {
          locationState?.labelGroupSetting ? (
            <>
              <Link
                to={setPath(URL.TASK_LABEL_SETTING.path, language)}
              >
                プロジェクト設定
              </Link>
            </>
          ) : (
            <>
              <Link
                to={setPath(URL.TASK.path, language)}
              >
                プロジェクトレポート
              </Link>
            </>
          )
        }
        &nbsp;&gt;&nbsp;
        プロジェクト並び替え
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          プロジェクト並び替え
        </h1>
      </section>
      <section className="page_description">
        <p>
          プロジェクトの表示順をドラッグアンドドロップで設定できます。
        </p>
        <p>
          プロジェクト設定画面、プロジェクトレポート画面にて、ソート条件に「カスタム設定」を選択いただくと、並び替えた順番が反映されます。
        </p>
        <p>
          また、業務仕分けボードの仕分け先選択時のプロジェクトの表示順に反映されます。
        </p>
        <p>
          (ユーザーごとで業務仕分けボードの仕分け先のプロジェクトを並び替えることが可能です。業務仕分けボードでは、ユーザーの並び替えたものが優先されます。)
        </p>
      </section>
      {
        pollingTrigger ? (
          <>
            <LoadingSquare>
              <>
                プロジェクトインポート処理が実行中です。しばらくおまちください。
              </>
            </LoadingSquare>
          </>
        ) : (
          <section className="page_contents content-area">
            <div className="content-block">
              <div className="block-detail">
                {
                  task.isLoading ? (
                    <LoadingSquare>
                      <>
                        プロジェクトを読み込んでいます…
                      </>
                    </LoadingSquare>
                  ) : (
                    <DndContext
                      autoScroll
                      sensors={sensors}
                      measuring={{
                        droppable: { strategy: MeasuringStrategy.BeforeDragging },
                      }}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                    >
                      <table className="order_table">
                        <thead>
                          <tr>
                            <th> </th>
                            <th>プロジェクト</th>
                            <th>予定期間</th>
                            <th>作成者</th>
                          </tr>
                        </thead>
                        <tbody>
                          <SortableContext
                            items={taskLabelGroupOrder ? taskLabelGroupOrder.map((i) => i.id) : []}
                            strategy={verticalListSortingStrategy}
                          >
                            {
                              taskLabelGroupOrder && taskLabelGroupOrder.map((v, i) => {
                                if (dragIds.length && ghostIds.includes(v.id)) return null
                                return (
                                  <SortableRow
                                    key={v.id}
                                    id={v.id}
                                    name={v.name}
                                    disable_status={v.disable_status}
                                    startDate={v.scheduled_start_date}
                                    endDate={v.scheduled_end_date}
                                    createdBy={v.created_by}
                                    orderIndex={v.order_index}
                                    color={v.color}
                                    isSelected={!!selectedRange && i >= selectedRange.lo && i <= selectedRange.hi}
                                    onClick={(e: React.MouseEvent<Element, MouseEvent>) => handleRowClick(e, v.id, i)}
                                  />
                                )
                              })
                            }
                          </SortableContext>
                        </tbody>
                      </table>
                      <DragOverlay dropAnimation={null}>
                        {overlay}
                      </DragOverlay>
                    </DndContext>
                  )
                }
              </div>
            </div>
          </section>
        )
      }
    </section>
  )
}

export default Index
